// src/pages/SaveTheDate.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import EventForm from '../components/EventForm';
import Carousel from '../components/Carousel';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 280px;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #b38b6d;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 40px 0 30px 0;
  flex-wrap: wrap;
`;

const ActionLink = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #5a4a42;
  padding: 12px 20px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 30px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(179, 139, 109, 0.2);
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  border: 1px solid rgba(179, 139, 109, 0.2);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(179, 139, 109, 0.3);
    background-color: white;
  }
`;

const ActionIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: cover;
`;

const CalendarIcon = styled.svg`
  width: 24px;
  height: 24px;
  margin-right: 12px;
  color: #b38b6d;
`;

const PageDecoration = styled.div`
  position: fixed;
  width: 300px;
  height: 300px;
  opacity: 0.15;
  z-index: -1;
  pointer-events: none;
  
  &.decoration-1 {
    top: 20px;
    left: 20px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cg fill='%23947060'%3E%3Cpath d='M100,30 C110,30 120,40 120,50 C120,60 110,70 100,70 C90,70 80,60 80,50 C80,40 90,30 100,30 Z'/%3E%3Cpath d='M100,10 C115,10 130,25 130,40 C130,55 115,70 100,70 C85,70 70,55 70,40 C70,25 85,10 100,10 Z' fill-opacity='0.6'/%3E%3Cpath d='M60,50 C70,50 80,60 80,70 C80,80 70,90 60,90 C50,90 40,80 40,70 C40,60 50,50 60,50 Z'/%3E%3Cpath d='M140,50 C150,50 160,60 160,70 C160,80 150,90 140,90 C130,90 120,80 120,70 C120,60 130,50 140,50 Z'/%3E%3Cpath d='M60,110 C70,110 80,120 80,130 C80,140 70,150 60,150 C50,150 40,140 40,130 C40,120 50,110 60,110 Z'/%3E%3Cpath d='M140,110 C150,110 160,120 160,130 C160,140 150,150 140,150 C130,150 120,140 120,130 C120,120 130,110 140,110 Z'/%3E%3Cpath d='M100,130 C110,130 120,140 120,150 C120,160 110,170 100,170 C90,170 80,160 80,150 C80,140 90,130 100,130 Z'/%3E%3C/g%3E%3Cpath d='M100,30 L100,10 M80,70 L60,90 M120,70 L140,90 M60,90 L60,110 M140,90 L140,110 M80,130 L100,130 M120,130 L140,110' stroke='%23947060' stroke-width='2' stroke-opacity='0.5' fill='none'/%3E%3C/svg%3E");
    transform: rotate(-10deg);
  }
  
  &.decoration-2 {
    bottom: 20px;
    right: 20px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cg fill='%23b38b6d'%3E%3Cpath d='M100,30 C110,30 120,40 120,50 C120,60 110,70 100,70 C90,70 80,60 80,50 C80,40 90,30 100,30 Z'/%3E%3Cpath d='M100,10 C115,10 130,25 130,40 C130,55 115,70 100,70 C85,70 70,55 70,40 C70,25 85,10 100,10 Z' fill-opacity='0.6'/%3E%3Cpath d='M60,50 C70,50 80,60 80,70 C80,80 70,90 60,90 C50,90 40,80 40,70 C40,60 50,50 60,50 Z'/%3E%3Cpath d='M140,50 C150,50 160,60 160,70 C160,80 150,90 140,90 C130,90 120,80 120,70 C120,60 130,50 140,50 Z'/%3E%3Cpath d='M60,110 C70,110 80,120 80,130 C80,140 70,150 60,150 C50,150 40,140 40,130 C40,120 50,110 60,110 Z'/%3E%3Cpath d='M140,110 C150,110 160,120 160,130 C160,140 150,150 140,150 C130,150 120,140 120,130 C120,120 130,110 140,110 Z'/%3E%3Cpath d='M100,130 C110,130 120,140 120,150 C120,160 110,170 100,170 C90,170 80,160 80,150 C80,140 90,130 100,130 Z'/%3E%3C/g%3E%3Cpath d='M100,30 L100,10 M80,70 L60,90 M120,70 L140,90 M60,90 L60,110 M140,90 L140,110 M80,130 L100,130 M120,130 L140,110' stroke='%23b38b6d' stroke-width='2' stroke-opacity='0.5' fill='none'/%3E%3C/svg%3E");
    transform: rotate(10deg);
  }
`;

const RegistrationMessage = styled.p`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.1rem;
  text-align: center;
  margin: 0 0 30px;
  padding: 15px 25px;
  background-color: transparent;
  border-radius: 8px;
  // border-left: 3px solid #d4b59e;
  // box-shadow: 0 2px 8px rgba(179, 139, 109, 0.1);
  max-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 25px;
    padding: 12px 20px;
  }
`;


const SaveTheDate = () => {
  const images = [
    '/img/card1.jpg',
    '/img/card2.jpg',
    '/img/card3.jpg',
    '/img/card4.jpg',
    '/img/card5.jpg',
    '/img/card6.jpg'
  ];

  // Preload images
  useEffect(() => {
    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <PageContainer>
      <PageDecoration className="decoration-1" />
      <PageDecoration className="decoration-2" />
      
      <PageTitle>
        Register 
        <NameSpan>Erika & Andrew</NameSpan>
      </PageTitle>

      <RegistrationMessage>
        Please fill out the form below to register for our wedding before submitting your RSVP!
      </RegistrationMessage>

      <Carousel images={images} />
      
      <ActionsContainer>
        <ActionLink 
          href="https://open.spotify.com/track/4JMC5leGU5JecMTybOuCpF#_=_" 
          target="_blank"
        >
          <ActionIcon src="/img/spotify_logo.jpg" alt="Spotify" />
          <span>Our Proposal Song</span>
        </ActionLink>
        
        <ActionLink href="/img/wedding_invite.ics" download>
          <CalendarIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </CalendarIcon>
          <span>Add to Calendar</span>
        </ActionLink>
      </ActionsContainer>
      
      <EventForm />
    </PageContainer>
  );
};

export default SaveTheDate;